import { useState, useEffect } from 'react';
import { MapPin, Calendar, Briefcase } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const HERO_BG = "https://images.unsplash.com/photo-1579003223313-8053dc9d10aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxNYWtrYWglMjBLYWFiYSUyMG5pZ2h0JTIwdmlldyUyMGhpZ2glMjBxdWFsaXR5fGVufDB8fHx8MTc3MDU0MDA3OHww&ixlib=rb-4.1.0&q=85";

export const VacanciesPage = () => {
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const { API } = useAuth();

  useEffect(() => {
    fetchVacancies();
  }, []);

  const fetchVacancies = async () => {
    try {
      const response = await fetch(`${API}/vacancies`);
      const data = await response.json();
      setVacancies(data);
    } catch (error) {
      console.error('Error fetching vacancies:', error);
      toast.error('Failed to load vacancies');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen pb-20">
      {/* Hero Section */}
      <section 
        className="relative py-20"
        style={{ 
          backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.9)), url(${HERO_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground mb-4">
            Teaching Vacancies
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Explore teaching opportunities for IRE educators across Kenya.
          </p>
        </div>
      </section>

      {/* Vacancies List */}
      <section className="py-8">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-heading font-bold text-primary">
              Open Positions
            </h2>
            <span className="text-sm text-muted-foreground">
              {vacancies.length} vacancies
            </span>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-muted-foreground mt-4">Loading vacancies...</p>
            </div>
          ) : vacancies.length === 0 ? (
            <div className="text-center py-12 glass-card rounded-2xl">
              <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-heading font-semibold text-foreground mb-2">No Vacancies Available</h3>
              <p className="text-muted-foreground">
                Check back later for new teaching opportunities.
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {vacancies.map((vacancy) => (
                <div 
                  key={vacancy.id}
                  className="glass-card rounded-xl p-6 hover:border-primary/50 transition-all"
                  data-testid={`vacancy-card-${vacancy.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Briefcase className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading font-bold text-lg text-foreground mb-2">{vacancy.title}</h3>
                      <p className="text-muted-foreground text-sm mb-4">{vacancy.description}</p>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-4 w-4 text-primary" />
                          {vacancy.location}
                        </span>
                        {vacancy.deadline && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4 text-primary" />
                            Deadline: {vacancy.deadline}
                          </span>
                        )}
                      </div>

                      <div className="bg-black/30 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-foreground mb-2">Requirements:</h4>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{vacancy.requirements}</p>
                      </div>

                      <div className="mt-4 text-xs text-muted-foreground">
                        Posted: {formatDate(vacancy.created_at)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
